/**
 * Fibonacci retracement renderer, extracted from Chart.tsx. Interactive
 * (anchor drag + per-fib menu), so it takes a `FibRenderCtx` carrying the
 * live handles + the resolved fib settings. Body is a verbatim move; only
 * the closed-over references became `ctx.*`.
 */
import type { MouseEvent, MutableRefObject, RefObject, ReactNode } from "react";
import type { IChartApi } from "lightweight-charts";
import {
  type FibRetracement,
  type SelectedDrawing,
  setSelectedDrawing,
} from "@/state/slices/uiSlice";
import type { AppDispatch } from "@/state/store";
import { startBodyDrag } from "@/tools/_shared/bodyDrag";
import type {
  selectEffectiveFibLevels,
  selectEffectiveFibLineWidth,
  selectEffectiveFibLineStyle,
  selectEffectiveFibDiagonal,
  selectEffectiveFibShowDiagonal,
  selectEffectiveFibLabelSide,
} from "@/state/selectors/effectiveTheme";
import { svgDasharrayFromName } from "./priceLineStyles";
import type { AnySeries } from "./chartData";
import type { DragTarget, MenuItem } from "./types";
import styles from "./Chart.module.css";

export interface FibRenderCtx {
  series: AnySeries | null;
  // Refs for the diagonal body-drag (grab the trend line to move the whole fib).
  seriesRef: MutableRefObject<AnySeries | null>;
  chartRef: MutableRefObject<IChartApi | null>;
  containerRef: RefObject<HTMLDivElement>;
  dragRef: MutableRefObject<DragTarget | null>;
  dispatch: AppDispatch;
  tabId: string;
  setIsDragging: (v: boolean) => void;
  timeToX: (time: number) => number | null;
  selectedDrawing: SelectedDrawing | null;
  startDrag: (
    target: Exclude<DragTarget, { type: "trading" } | { type: "draft" }>,
  ) => (e: MouseEvent) => void;
  startLockedPan: (nativeEvent: globalThis.MouseEvent) => void;
  showMenu: (items: MenuItem[]) => (e: MouseEvent) => void;
  fibLevels: ReturnType<typeof selectEffectiveFibLevels>;
  fibLineWidth: ReturnType<typeof selectEffectiveFibLineWidth>;
  fibLineStyle: ReturnType<typeof selectEffectiveFibLineStyle>;
  fibDiagonal: ReturnType<typeof selectEffectiveFibDiagonal>;
  fibShowDiagonal: ReturnType<typeof selectEffectiveFibShowDiagonal>;
  fibLabelSide: ReturnType<typeof selectEffectiveFibLabelSide>;
  fibMenuItems: (fibId: string) => MenuItem[];
  digits: number;
  /** On-chart text colour (chrome.textColor) — tracks the chart background
   *  so labels stay readable in both light and dark themes. */
  textColor: string;
}

export function renderFib(
  fib: FibRetracement,
  opacity: number,
  key: string,
  ctx: FibRenderCtx,
): ReactNode {
  const series = ctx.series;
  if (!series) return null;
  if (fib.visible === false) return null;

  const startX = ctx.timeToX(fib.startTime);
  const endX = ctx.timeToX(fib.endTime);
  const startY = series.priceToCoordinate(fib.startPrice);
  const endY = series.priceToCoordinate(fib.endPrice);

  if (startX == null || endX == null || startY == null || endY == null) return null;

  const left = Math.min(startX, endX);
  const right = Math.max(startX, endX);
  // Label side: per-fib override wins, else the global default.
  const labelOnLeft = (fib.labelSide ?? ctx.fibLabelSide) === "left";
  const labelX = labelOnLeft ? left - 6 : right + 6;
  const labelAnchor = labelOnLeft ? "end" : "start";
  const range = fib.endPrice - fib.startPrice;
  const isPreview = key.startsWith("preview");
  const isSelected =
    !isPreview &&
    ctx.selectedDrawing?.kind === "fib" &&
    ctx.selectedDrawing.id === fib.id;

  // Shared stroke for every level + the connecting trend line.
  const sharedWidth = Math.max(1, Math.min(4, ctx.fibLineWidth));
  const sharedDash = svgDasharrayFromName(ctx.fibLineStyle);

  return (
    <g key={key} opacity={opacity}>
      {/* Diagonal/trend line connecting the two anchors. Visibility: per-fib
          `hideDiagonal` override wins, else the global default. */}
      {(fib.hideDiagonal != null ? !fib.hideDiagonal : ctx.fibShowDiagonal) && (
        <line
          x1={startX}
          y1={startY}
          x2={endX}
          y2={endY}
          stroke={isSelected ? "#3b82f6" : ctx.fibDiagonal.color}
          strokeWidth={
            isSelected ? ctx.fibDiagonal.width + 0.5 : ctx.fibDiagonal.width
          }
          strokeDasharray={svgDasharrayFromName(ctx.fibDiagonal.style)}
          pointerEvents="none"
        />
      )}

      {/* One bounded horizontal line + label per enabled fib level. A level
          also drops out if this specific fib hides it. */}
      {ctx.fibLevels
        .filter((l) => l.enabled && !fib.hiddenLevels?.includes(l.ratio))
        .map(({ ratio, color, opacity }) => {
          const price = fib.startPrice + ratio * range;
          const y = series.priceToCoordinate(price);
          if (y == null) return null;
          const lineOpacity = opacity ?? 1;
          return (
            <g key={ratio}>
              {/* Level line + label are visual only — `pointerEvents: none` so a
                  click over them passes through to the chart (lets you draw a new
                  drawing overlapping this fib). The fib is grabbed only at its two
                  endpoint handles + the diagonal. */}
              <line
                x1={left}
                y1={y}
                x2={right}
                y2={y}
                stroke={color}
                strokeWidth={sharedWidth}
                strokeDasharray={sharedDash}
                strokeOpacity={lineOpacity}
                pointerEvents="none"
              />
              <text
                x={labelX}
                y={y + 4}
                textAnchor={labelAnchor}
                fill={color}
                fillOpacity={lineOpacity}
                fontSize={11}
                pointerEvents="none"
              >
                {(ratio * 100).toFixed(1)}% ({price.toFixed(ctx.digits)})
              </text>
            </g>
          );
        })}

      {/* Price range across the fib (0% = start anchor, 100% = end anchor). */}
      <text
        x={right}
        y={Math.min(startY, endY) - 5}
        textAnchor="end"
        fill={ctx.textColor}
        fontSize={11}
        fontWeight={600}
        pointerEvents="none"
      >
        {Math.floor(Math.abs(range) * 10)} pips
      </text>

      {/* Anchor handles — drag-rearrange. Hidden in preview mode. */}
      {!isPreview && (
        <>
          {/* Invisible thick grab line over the diagonal — the ONLY move zone.
              Drag to MOVE the whole fib. Rendered before the endpoint handles so
              the circles keep priority for reshaping the ends. Level lines are
              non-interactive so clicks elsewhere pass through (draw overlapping). */}
          <line
            x1={startX}
            y1={startY}
            x2={endX}
            y2={endY}
            stroke="transparent"
            strokeWidth={10}
            className={styles.posBodyHit}
            data-drawing-hit="true"
            onMouseDown={fib.locked
              ? (e: MouseEvent) => { if (e.button !== 0) return; ctx.startLockedPan(e.nativeEvent); ctx.dispatch(setSelectedDrawing({ tabId: ctx.tabId, selected: { kind: "fib", id: fib.id } })); }
              : (e) => startBodyDrag(e, ctx, "fib", fib)}
            onContextMenu={ctx.showMenu(ctx.fibMenuItems(fib.id))}
          />
          <circle
            cx={startX}
            cy={startY}
            r={isSelected ? 6 : 5}
            fill={isSelected ? "#3b82f6" : "#161922"}
            stroke={isSelected ? "#ffffff" : "#9aa0a6"}
            strokeWidth={1.5}
            className={styles.fibHandle}
            data-drawing-hit="true"
            onMouseDown={fib.locked
              ? (e: MouseEvent) => { if (e.button !== 0) return; ctx.startLockedPan(e.nativeEvent); ctx.dispatch(setSelectedDrawing({ tabId: ctx.tabId, selected: { kind: "fib", id: fib.id } })); }
              : ctx.startDrag({ type: "fib", id: fib.id, anchor: "start" })}
            onContextMenu={ctx.showMenu(ctx.fibMenuItems(fib.id))}
          />
          <circle
            cx={endX}
            cy={endY}
            r={isSelected ? 6 : 5}
            fill={isSelected ? "#3b82f6" : "#161922"}
            stroke={isSelected ? "#ffffff" : "#9aa0a6"}
            strokeWidth={1.5}
            className={styles.fibHandle}
            data-drawing-hit="true"
            onMouseDown={fib.locked
              ? (e: MouseEvent) => { if (e.button !== 0) return; ctx.startLockedPan(e.nativeEvent); ctx.dispatch(setSelectedDrawing({ tabId: ctx.tabId, selected: { kind: "fib", id: fib.id } })); }
              : ctx.startDrag({ type: "fib", id: fib.id, anchor: "end" })}
            onContextMenu={ctx.showMenu(ctx.fibMenuItems(fib.id))}
          />
        </>
      )}
    </g>
  );
}
